import './HomePage.css';

import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {useAuth} from '../../../contexts/AuthContext';
import {useDashboardReport} from '../../../hooks/useDashboardReport';
import {useUserInfo} from '../../../hooks/useUserInfo';
import {parseISOWithoutConversion} from '../../../utils';
import CheckedInToday from './CheckedInToday';
import ComplianceChart from './ComplianceChart';
import ImpairedEmployeesChart from './ImpairedEmployeesChart';
import ImpairmentAlertsToday from './ImpairmentAlertsToday';
import SiteBarChart from './SiteBarChart';

const HomePage = () => {
  const {currentUser} = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [checkedInToday, setCheckedInToday] = useState(0);

  const {
    isMultiOrg,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useUserInfo();

  const currentDate = new Date();
  const todayDateStr = currentDate.toISOString().split('T')[0];

  // Fetch today's data for alerts and check-in count
  const {loading, error, recordingsData} = useDashboardReport({
    uid: currentUser?.uid,
    startDate: todayDateStr,
    endDate: todayDateStr,
    enabled: !userInfoLoading && !userInfoError && !!currentUser?.uid,
  });

  const handleBaselineNavigation = () => {
    navigate('/dashboard/baseline');
  };

  // Process today's data for alerts and check-in count
  useEffect(() => {
    if (recordingsData.length === 0) {
      setAlerts([]);
      setCheckedInToday(0);
      return;
    }

    // Filter flagged recordings for today's alerts
    const flaggedRecordings = recordingsData
      .filter((rec) => rec.result === '1' || rec.result === 1)
      .sort((a, b) => {
        const parsedA = parseISOWithoutConversion(
          a.checkin_datetime_in_org_zone,
        );
        const parsedB = parseISOWithoutConversion(
          b.checkin_datetime_in_org_zone,
        );
        const dateA = parsedA ? parsedA.toDate().getTime() : 0;
        const dateB = parsedB ? parsedB.toDate().getTime() : 0;
        return dateB - dateA;
      });

    const alertsData = flaggedRecordings.map((rec) => {
      const parsed = parseISOWithoutConversion(
        rec.checkin_datetime_in_org_zone,
      );
      return {
        name: `${rec.first_name} ${rec.last_name}`,
        company: rec.company_name || 'Unknown Organization',
        date: parsed ? parsed.toDate() : new Date(),
      };
    });

    setAlerts(alertsData);

    // Count unique employees checked in today
    const uniqueEmployeeIds = new Set();
    recordingsData.forEach((rec) => {
      const employeeId =
        rec.employee_id || `${rec.first_name}_${rec.last_name}`;
      uniqueEmployeeIds.add(employeeId);
    });
    setCheckedInToday(uniqueEmployeeIds.size);
  }, [recordingsData]);

  if (userInfoError) {
    return <div className='error'>Failed to determine user permissions</div>;
  }

  if (loading || userInfoLoading) {
    return <div className='loading'>Loading data...</div>;
  }

  if (error) {
    return <div className='error'>{error}</div>;
  }

  return (
    <div className='home-content'>
      <ImpairmentAlertsToday alerts={alerts} />

      {isMultiOrg ? (
        <SiteBarChart
          uid={currentUser?.uid}
          enabled={!userInfoLoading && !!currentUser?.uid}
        />
      ) : (
        <CheckedInToday
          checkedInToday={checkedInToday}
          onBaselineClick={handleBaselineNavigation}
        />
      )}

      <div className='charts-section'>
        <ComplianceChart
          uid={currentUser?.uid}
          enabled={!userInfoLoading && !!currentUser?.uid}
        />
        <ImpairedEmployeesChart
          uid={currentUser?.uid}
          enabled={!userInfoLoading && !!currentUser?.uid}
        />
      </div>
    </div>
  );
};

export default HomePage;
